import {
  Inject,
  Injectable,
  PLATFORM_ID,
  Optional,
  Renderer2,
  RendererFactory2,
  ViewEncapsulation
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import { ThemeService } from '../theme';
import { NoolaConfig } from './noola-config.service';
import { NoolaBootInput } from './noola.model';

/**
 * A provider wrapping the Noola widget JS API (https://api.noola.cc/widget.js).
 *
 * Mirrors the Intercom wrapper: a global `Noola(...)` command queue is created
 * up-front so calls are safe before the async widget script finishes loading.
 */
@Injectable({ providedIn: 'root' })
export class Noola {

  private _renderer2: Renderer2;
  private _injected = false;
  private _themeSub?: Subscription;

  constructor(
    @Inject(NoolaConfig)
    private _config: NoolaConfig,
    @Inject(PLATFORM_ID)
    protected _platformId: any,
    private _rendererFactory: RendererFactory2,
    private _theme: ThemeService,
    @Optional() @Inject(DOCUMENT)
    private _document: any // Document
  ) {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    this._renderer2 = this._rendererFactory.createRenderer(this._document, {
      id: '-1',
      encapsulation: ViewEncapsulation.None,
      styles: [],
      data: {}
    });
  }

  /**
   * Loads the widget (once) and identifies the current visitor. Subsequent
   * calls just update the identified visitor's attributes.
   */
  boot(data?: NoolaBootInput) {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    const firstBoot = !this._injected;

    if (firstBoot) {
      this._injectScript();
    }

    // Keep the widget's colour scheme locked to the app's theme (light / dark / system, already
    // resolved to a boolean by ThemeService). Subscribing here fires synchronously with the current
    // value — queued ahead of the `boot` command below, so the widget mounts in the right theme with
    // no flash — and again on every later toggle.
    this._syncTheme();

    return this._call(firstBoot ? 'boot' : 'update', data);
  }

  /**
   * Pin the widget's colour scheme. `ThemeService` drives this automatically via `boot()`; call it
   * directly only to override.
   */
  setTheme(mode: 'light' | 'dark' | 'auto') {
    return this._call('theme', mode);
  }

  private _syncTheme() {
    if (this._themeSub || !isPlatformBrowser(this._platformId)) {
      return;
    }
    this._themeSub = this._theme.isDarkMode$.subscribe((isDark) => this.setTheme(isDark ? 'dark' : 'light'));
  }

  /**
   * Update the identified visitor's attributes.
   */
  update(data?: NoolaBootInput) {
    return this._call('update', data);
  }

  /**
   * Open / close the chat panel.
   */
  open() {
    return this._call('open');
  }

  close() {
    return this._call('close');
  }

  /**
   * Show / hide the launcher bubble.
   */
  show() {
    return this._call('show');
  }

  hide() {
    return this._call('hide');
  }

  /**
   * Log a custom activity event.
   */
  track(eventName: string, metadata?: any) {
    return this._call('track', eventName, metadata);
  }

  /**
   * Clear identity + reset (e.g. on logout).
   */
  shutdown() {
    return this._call('shutdown');
  }

  /**
   * Fires when the chat panel opens.
   */
  onOpen(handler: () => void) {
    this._addWindowListener('noola:open', handler);
  }

  /**
   * Fires when the chat panel closes.
   */
  onClose(handler: () => void) {
    this._addWindowListener('noola:close', handler);
  }

  private _addWindowListener(name: string, handler: () => void) {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    window.addEventListener(name, handler);
  }

  /**
   * Private handler to run Noola commands safely.
   */
  private _call(fn: string, ...args: any[]) {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    const w = window as any;
    if (typeof w.Noola === 'function') {
      return w.Noola(fn, ...args.filter((arg) => arg !== undefined));
    }
    return;
  }

  private _injectScript() {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    const w = window as any;

    // Create the command queue stub so calls made before the async widget
    // script finishes loading are queued and replayed once it loads.
    if (typeof w.Noola !== 'function') {
      const n: any = function (...args: any[]) {
        n.q.push(args);
      };
      n.q = [];
      w.Noola = n;
    }

    const s = this._document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = `${this._config.api_url}/widget.js`;
    s.setAttribute('data-noola-key', this._config.widget_key);
    s.setAttribute('data-noola-api', this._config.api_url);
    s.setAttribute('data-noola-edge', this._config.edge_url);

    if (this._config.hidden) {
      s.setAttribute('data-noola-hidden', '');
    }
    if (this._config.accent) {
      s.setAttribute('data-noola-accent', this._config.accent);
    }
    if (this._config.title) {
      s.setAttribute('data-noola-title', this._config.title);
    }

    if (this._renderer2 && this._renderer2.appendChild) {
      this._renderer2.appendChild(this._document.head, s);
    }

    this._injected = true;
  }

}
